import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { Home } from "lucide-react";
import { formatPrice, propertyImage } from "@/lib/api";

const INDIA_CENTER = [21.146, 79.088];

const priceIcon = (label, active) =>
  L.divIcon({
    className: "",
    html: `<div style="transform:translate(-50%,-50%);white-space:nowrap;background:${
      active ? "#ff385c" : "#ffffff"
    };color:${active ? "#ffffff" : "#222222"};border:1px solid rgba(0,0,0,.12);border-radius:9999px;
    padding:6px 12px;font:700 12px/1 'Nunito Sans',sans-serif;box-shadow:0 2px 8px rgba(0,0,0,.18)">${label}</div>`,
    iconSize: [0, 0],
  });

const PLACE_COLORS = { school: "#0f766e", hospital: "#b91c1c", metro: "#1d4ed8" };

const placeIcon = (kind) =>
  L.divIcon({
    className: "",
    html: `<div style="transform:translate(-50%,-50%);width:14px;height:14px;border-radius:9999px;
    background:${PLACE_COLORS[kind] || "#555"};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></div>`,
    iconSize: [0, 0],
  });

const FitBounds = ({ points, homeRef }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
    if (homeRef) homeRef.current = () => map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
  }, [map, points, homeRef]);
  return null;
};

export const PropertyMap = ({ items = [], places = [], height = "72vh" }) => {
  const homeRef = useRef(null);
  const pins = useMemo(() => items.filter((p) => p.lat && p.lng), [items]);
  const points = useMemo(() => pins.map((p) => [p.lat, p.lng]), [pins]);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border" style={{ height }} data-testid="property-map">
      <MapContainer center={points[0] || INDIA_CENTER} zoom={points.length ? 11 : 5} scrollWheelZoom className="h-full w-full">
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} homeRef={homeRef} />
        {pins.map((p) => (
          <Marker key={p.property_id} position={[p.lat, p.lng]} icon={priceIcon(formatPrice(p.price, p.listing_type), false)}>
            <Popup>
              <Link to={`/property/${p.property_id}`} className="block w-52" data-testid={`map-popup-${p.property_id}`}>
                <img src={propertyImage(p)} alt={p.title} className="h-28 w-full rounded-lg object-cover" />
                <p className="mt-2 text-[13px] font-bold text-foreground">{formatPrice(p.price, p.listing_type)}</p>
                <p className="text-[12px] text-muted-foreground line-clamp-2">{p.title}</p>
                <p className="text-[11px] text-muted-foreground">
                  {p.locality ? `${p.locality}, ` : ""}
                  {p.city}
                </p>
              </Link>
            </Popup>
          </Marker>
        ))}
        {places
          .filter((pl) => pl.lat && pl.lng)
          .map((pl) => (
            <Marker key={`pl-${pl.id}`} position={[pl.lat, pl.lng]} icon={placeIcon(pl.kind)}>
              <Popup>
                <span className="block w-40 text-[12px] font-semibold" data-testid={`nearby-popup-${pl.id}`}>
                  {pl.name}
                  <span className="block font-normal text-muted-foreground">
                    {pl.kind} ·{" "}
                    {pl.distance_m < 1000 ? `${pl.distance_m} m away` : `${(pl.distance_m / 1000).toFixed(1)} km away`}
                  </span>
                </span>
              </Popup>
            </Marker>
          ))}
      </MapContainer>

      <button
        onClick={() => homeRef.current?.()}
        className="absolute right-3 top-3 z-[500] flex items-center gap-2 rounded-full bg-background px-4 py-2.5 text-xs font-bold shadow-panel hover:bg-muted transition-colors duration-200"
        data-testid="map-home-btn"
      >
        <Home className="h-4 w-4" /> Home view
      </button>

      {pins.length === 0 && (
        <div className="absolute inset-0 z-[400] grid place-items-center bg-background/80" data-testid="map-empty">
          <p className="text-sm text-muted-foreground">No mapped properties for these filters yet.</p>
        </div>
      )}
    </div>
  );
};

export default PropertyMap;
